// Motifs proposés par défaut pour l'affichage du squelette
export const SKELETON_PLACEHOLDERS_PATTERNS = {
    CARD: {
        className: 'skeleton-placeholder--card',
        childrenCount: 8
    },
    LIST: {
        className: 'skeleton-placeholder--list',
        childrenCount: 9
    },
    TABLE: {
        className: 'skeleton-placeholder--table',
        childrenCount: 15
    },
    AVATAR_LEFT: {
        className: 'skeleton-placeholder--avatar-left',
        childrenCount: 4
    },
    AVATAR_RIGHT: {
        className: 'skeleton-placeholder--avatar-right',
        childrenCount: 4
    },
    PROFILE_LEFT: {
        className: 'skeleton-placeholder--profile-left',
        childrenCount: 8
    },
    PROFILE_RIGHT: {
        className: 'skeleton-placeholder--profile-right',
        childrenCount: 8
    },
    IMAGE: {
        className: 'skeleton-placeholder--image',
        childrenCount: 1
    },
    TEXT: {
        className: 'skeleton-placeholder--text',
        childrenCount: 12
    },
    GRID: {
        className: 'skeleton-placeholder--grid',
        childrenCount: 12
    },
};

// Mode de choix des motifs
export const SKELETON_PLACEHOLDERS_MODE = {
    RANDOM: 'random',
    SEQUENTIAL: 'sequential'
};

/**
 * Crée un squelette d'affichage au sein d'un élément DOM donné, permettant à un élément réel de prendre le temps de se charger en arrière-plan.
 *
 * @param {HTMLElement} element Élément du DOM englobant.
 * @param {Object} [options={}] Options de configuration.
 * @param {Array} [options.patterns=SKELETON_PLACEHOLDERS_PATTERNS] Tableau contenant les définitions des motifs pour les squelettes.
 * @param {number} [options.count=6] Le nombre de motifs à générer.
 * @param {number} [options.refreshRate=1000] Le délai à attendre, en millisecondes, avant lequel le squelette est régénéré.
 * @param {string} [options.mode=SKELETON_PLACEHOLDERS_MODE.RANDOM] Le mode de sélection des motifs. Peut être "RANDOM" ou "SEQUENTIAL".
 * @param {string} [options.loadingClassName='u-skeleton-placeholder-loading'] La classe CSS à ajouter à l'élément pendant l'état de chargement.
 *
 * @return {Function} Une fonction de nettoyage pour arrêter le fonctionnement en mode squelette.
 */
export function useSkeletonPlaceholder(element, {
    patterns = SKELETON_PLACEHOLDERS_PATTERNS,
    count = 6,
    refreshRate = 1_000,
    mode = SKELETON_PLACEHOLDERS_MODE.RANDOM,
    loadingClassName = 'u-skeleton-placeholder-loading'
} = { }) {
    let timeout = null;

    /**
     * Récupère la clé suivante en fonction du mode sélectionné.
     *
     * @param {number} index - L'index actuel utilisé pour déterminer la clé suivante en mode séquentiel.
     * @return {string} La clé suivante parmi les clés de motif disponibles, déterminée de manière aléatoire ou séquentielle en fonction du mode sélectionné.
     */
    function nextKey(index) {
        const patternKeys = Object.keys(patterns);
        let key;
        switch (mode) {
            case SKELETON_PLACEHOLDERS_MODE.RANDOM: {
                key = patternKeys[Math.floor(Math.random() * patternKeys.length)];
                break;
            }
            case SKELETON_PLACEHOLDERS_MODE.SEQUENTIAL: {
                key = patternKeys[(index % patternKeys.length)];
                break;
            }
        }
        return key;
    }

    /**
     * Remplit l'élément cible avec des motifs de squelette.
     */
    function fill() {
        timeout = setTimeout(() => {
            fill();
        }, refreshRate);

        element.innerHTML = '';
        element.classList.add(loadingClassName);

        const placeholders = Array(count).fill(null).map((_, index) => {
            const key = nextKey(index);
            const pattern = patterns[key];
            return `<div class="skeleton-placeholder ${pattern.className}" style="--index: ${index}">
                ${Array(pattern.childrenCount).fill(null).map((_, i) => `<div class="skeleton-placeholder__item skeleton-placeholder__item--${i}"></div>`).join('')}
            </div>`;
        });

        element.innerHTML = placeholders.join('');
    }

    fill();
    /**
     * La fonction renvoyée permet de stopper le fonctionnement.
     *
     * @param {boolean} emptyElement Indique s'il faut vider ou non l'élément englobant.
     */
    return (emptyElement = false) => {
        clearTimeout(timeout);
        if (emptyElement) {
            element.innerHTML = '';
        }
        element.classList.remove(loadingClassName);
    };
}